import { useCallback, useEffect, useState } from 'react';
import {
  builtInCurriculum, newId, sanitizeConfig,
  type Curriculum, type CurriculumLevel, type CurriculumStage, type Grade, type LevelConfig,
} from '@numslearn/shared';
import { api } from '../net/api.js';

/**
 * A teacher's curricula.
 *
 * The built-in Grades 1–6 always come back as a read-only starter — that is
 * what "Duplicate" copies — so a teacher who has never opened the editor still
 * sees something recognisable, and the app behaves exactly as it did before.
 */

interface Listing { builtin: Curriculum; mine: Curriculum[] }

export interface CurriculaStore {
  builtin: Curriculum;
  mine: Curriculum[];
  /** True once the server has confirmed a signed-in teacher. Curricula are saved to an account. */
  available: boolean;
  loading: boolean;
  saving: boolean;
  save: (cur: Curriculum) => Promise<Curriculum | null>;
  remove: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useCurricula(): CurriculaStore {
  const [builtin, setBuiltin] = useState<Curriculum>(() => builtInCurriculum());
  const [mine, setMine] = useState<Curriculum[]>([]);
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const list = await api.get<Listing>('/curricula');
      setBuiltin(list.builtin);
      setMine(list.mine);
      setAvailable(true);
    } catch {
      // Not signed in: the built-in one still works, and nothing can be saved.
      setMine([]);
      setAvailable(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const save = useCallback(async (cur: Curriculum): Promise<Curriculum | null> => {
    setSaving(true);
    try {
      const saved = await api.put<Curriculum>(`/curricula/${encodeURIComponent(cur.id)}`, cur);
      // The server sanitises on the way in, so what comes back may differ from
      // what went out — a clamped board size, a defaulted rule. Take its word.
      setMine((list) => {
        const without = list.filter((c) => c.id !== saved.id);
        return [saved, ...without];
      });
      return saved;
    } catch {
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const remove = useCallback(async (id: string) => {
    try {
      await api.del(`/curricula/${encodeURIComponent(id)}`);
      setMine((list) => list.filter((c) => c.id !== id));
    } catch {
      /* the listing is refreshed on the next open */
    }
  }, []);

  return { builtin, mine, available, loading, saving, save, remove, refresh };
}

/* ── editing helpers ───────────────────────────────────────────────────────
 * All pure, all returning a new curriculum: the editor keeps one object in
 * state and replaces it, so undo is a matter of not having saved yet.
 * ─────────────────────────────────────────────────────────────────────────── */

export const emptyCurriculum = (name: string): Curriculum => ({
  id: newId('cur'),
  name,
  stages: [],
  updatedAt: new Date().toISOString(),
});

/** A copy of the built-in curriculum with fresh ids, so edits do not alias it. */
export function duplicateCurriculum(source: Curriculum, name: string): Curriculum {
  return {
    id: newId('cur'),
    name,
    updatedAt: new Date().toISOString(),
    stages: source.stages.map((s) => ({
      ...s,
      id: newId('stg'),
      levels: s.levels.map((l) => ({ ...l, id: newId('lvl'), config: { ...l.config } })),
    })),
  };
}

export const newStage = (grade: Grade, name: string): CurriculumStage => ({
  id: newId('stg'), name, grade, levels: [],
});

export const newLevel = (name: string, config: LevelConfig): CurriculumLevel => ({
  id: newId('lvl'), name, config: sanitizeConfig(config),
});

export function mapStage(
  cur: Curriculum, stageId: string, fn: (s: CurriculumStage) => CurriculumStage,
): Curriculum {
  return { ...cur, stages: cur.stages.map((s) => (s.id === stageId ? fn(s) : s)) };
}

export function mapLevel(
  cur: Curriculum, stageId: string, levelId: string, fn: (l: CurriculumLevel) => CurriculumLevel,
): Curriculum {
  return mapStage(cur, stageId, (s) => ({
    ...s, levels: s.levels.map((l) => (l.id === levelId ? fn(l) : l)),
  }));
}

/** Moves an item one place, clamped at both ends. Used for stages and levels. */
export function move<T>(items: T[], index: number, delta: number): T[] {
  const to = index + delta;
  if (index < 0 || index >= items.length || to < 0 || to >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item!);
  return next;
}
